<script>
  import { createEventDispatcher } from 'svelte';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';

  export let showModal = false;
  export let preSelectedStores = [];

  const dispatch = createEventDispatcher();

  let allStores = [];
  let allCategories = [];
  let selectedCategories = [];
  let filteredStores = [];
  let selectedStores = []; 
  let searchQuery = '';
  let isLoading = false;
  let error = '';
  let wasOpen = false;

  $: if (showModal) {
    if (!wasOpen) {
      wasOpen = true;
      initialize();
    }
  } else {
    wasOpen = false;
  }

  function initialize() {
    selectedStores = [...preSelectedStores];
    searchQuery = '';
    selectedCategories = [];
    loadData();
  }

  async function loadData() {
    isLoading = true;
    error = '';
    try {
      const [storesRes, catsRes] = await Promise.all([
        fetch(getApiUrl(`/stores?countryCode=${$selectedCountry}`)),
        fetch(getApiUrl('/categories'))
      ]);

      if (!storesRes.ok) throw new Error($t('store_modal.error_loading'));

      const storesData = await storesRes.json();
      if (catsRes.ok) {
        allCategories = await catsRes.json();
      } else {
        allCategories = [];
      }

      allStores = (storesData || []).map(s => {
        const aliasStr = (s.aliases && s.aliases.length > 0) ? ` (${s.aliases.join(', ')})` : '';
        return {
          _id: s._id,
          name: s.name || '',
          displayName: `${s.name || ''}${aliasStr}`,
          aliases: s.aliases || [],
          categories: s.categories || []
        };
      });
    } catch (e) {
      console.error('Error loading stores/categories:', e);
      error = e.message || $t('store_modal.error_loading');
    } finally {
      isLoading = false;
    }
  }

  $: {
    const query = searchQuery.trim().toLowerCase();

    let list = allStores.filter(store => {
      // 1. Keyword search (matches store name or any alias)
      const matchesKeyword = !query ||
        (store.name && store.name.toLowerCase().includes(query)) ||
        (store.aliases && store.aliases.some(a => a.toLowerCase().includes(query)));

      // 2. Category matching
      // If no categories are selected, show all stores (matching keyword).
      // If categories are selected, show all stores with matching categories.
      const matchesCategory = selectedCategories.length === 0 ||
        (store.categories && store.categories.some(cat => selectedCategories.includes(cat)));

      return matchesKeyword && matchesCategory;
    });

    // Sort by store name alphabetically
    list.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true }));

    filteredStores = list;
  }

  function toggleCategory(catName) {
    if (selectedCategories.includes(catName)) {
      selectedCategories = selectedCategories.filter(c => c !== catName);
    } else {
      selectedCategories = [...selectedCategories, catName];
    }
  }

  function clearCategories() {
    selectedCategories = [];
  }

  function isStoreSelected(store) {
    return selectedStores.includes(store.displayName) || selectedStores.includes(store.name);
  }

  function toggleStore(store, isChecked) {
    const value = store.displayName;
    if (isChecked) {
      if (!selectedStores.includes(value) && !selectedStores.includes(store.name)) {
        selectedStores = [...selectedStores, value];
      }
    } else {
      selectedStores = selectedStores.filter(s => s !== value && s !== store.name);
    }
  }

  function addCustomStore() {
    const trimmed = searchQuery.trim();
    if (trimmed && !selectedStores.includes(trimmed)) {
      selectedStores = [...selectedStores, trimmed];
      searchQuery = '';
    }
  }

  function removeSelected(store) {
    selectedStores = selectedStores.filter(s => s !== store);
  }

  function confirmSelection() {
    dispatch('confirm', { selected: selectedStores });
    closeModal();
  }

  function closeModal() {
    dispatch('close');
  }
</script>

{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h3>{$t('store_modal.title')} ({$selectedCountry})</h3>
        <button class="close-btn" on:click={closeModal} aria-label="Close">&times;</button>
      </div>
      
      <!-- Search and Add row -->
      <div class="search-section">
        <div class="input-row">
          <input 
            type="text" 
            placeholder={$t('store_modal.search_placeholder')} 
            bind:value={searchQuery}
            on:keydown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomStore())}
          />
          <button class="add-btn" on:click={addCustomStore} disabled={!searchQuery.trim()}>
            {$t('store_modal.add_btn')}
          </button>
        </div>
      </div>

      <!-- Categories Filter Section -->
      {#if allCategories && allCategories.length > 0}
        <div class="category-filter-section">
          <div class="category-header">
            <span class="category-title">{$t('store_modal.filter_by_category')}</span>
            {#if selectedCategories.length > 0}
              <button class="clear-categories-btn" type="button" on:click={clearCategories}>
                {$t('store_modal.clear_categories')} &times;
              </button>
            {/if}
          </div>
          <div class="category-chips-container">
            <button 
              type="button" 
              class="cat-chip" 
              class:active={selectedCategories.length === 0}
              on:click={clearCategories}
            >
              {$t('store_modal.all_categories')}
            </button>
            {#each allCategories as cat (cat._id || cat.name)}
              <button 
                type="button" 
                class="cat-chip" 
                class:active={selectedCategories.includes(cat.name)}
                on:click={() => toggleCategory(cat.name)}
              >
                {cat.name}
              </button>
            {/each}
          </div>
        </div>
      {/if}
      
      <!-- Selections Area -->
      <div class="selections">
        <strong>{$t('store_modal.selected_count')} ({selectedStores.length}):</strong>
        <div class="chips-container">
          {#each selectedStores as store}
            <span class="chip">
              {store} 
              <button class="remove" on:click={() => removeSelected(store)}>&times;</button>
            </span>
          {/each}
        </div>
      </div>

      <!-- Stores List -->
      {#if isLoading}
        <p class="status-msg">{$t('store_modal.loading')}</p>
      {:else if error}
        <p class="status-msg error">{error}</p>
      {:else}
        <div class="store-list">
          {#each filteredStores as store (store._id || store.displayName)}
            <div class="store-item" class:is-selected={isStoreSelected(store)}>
              <label>
                <input 
                  type="checkbox" 
                  value={store.displayName} 
                  checked={isStoreSelected(store)}
                  on:change={(e) => toggleStore(store, e.target.checked)}
                />
                <div class="store-details">
                  <span class="store-name">{store.displayName}</span>
                  {#if store.categories && store.categories.length > 0}
                    <div class="store-badges">
                      {#each store.categories as cat}
                        <span class="badge" class:highlighted={selectedCategories.includes(cat)}>{cat}</span>
                      {/each}
                    </div>
                  {/if}
                </div>
              </label>
            </div>
          {/each}
          {#if filteredStores.length === 0}
            {#if searchQuery}
              <p class="no-results">
                {$t('store_modal.no_results_add_prompt').replace('{name}', searchQuery)}
              </p>
            {:else}
              <p class="no-results">{$t('store_modal.no_stores_found')}</p>
            {/if}
          {/if}
        </div>
      {/if}

      <div class="modal-actions">
        <button class="secondary" on:click={closeModal}>{$t('store_modal.cancel_btn')}</button>
        <button class="primary" on:click={confirmSelection}>{$t('store_modal.confirm_btn')}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop { 
    position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
    background: rgba(0, 0, 0, 0.5); 
    display: flex; justify-content: center; align-items: center; 
    z-index: 1000; 
  }
  .modal-content { 
    background: white; padding: 1.5rem; border-radius: 8px; 
    width: 95%; max-width: 1200px; max-height: 85vh; 
    display: flex; flex-direction: column; 
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  }

  .modal-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 1rem; border-bottom: 1px solid #eee; padding-bottom: 0.5rem;
  }
  .modal-header h3 { margin: 0; font-size: 1.25rem; }
  .close-btn { 
    background: none; border: none; font-size: 1.6rem; 
    cursor: pointer; color: #888; line-height: 1; padding: 0 0.3rem; 
  }
  .close-btn:hover { color: #333; }
  
  .search-section { margin-bottom: 0.75rem; }
  .input-row { display: flex; gap: 0.5rem; }
  input[type="text"] { 
    flex-grow: 1; padding: 0.5rem 0.75rem; border: 1px solid #ccc; 
    border-radius: 4px; font-size: 0.95rem; 
  }
  input[type="text"]:focus {
    outline: none; border-color: #007bff; box-shadow: 0 0 0 2px rgba(0,123,255,0.2);
  }
  .add-btn { 
    padding: 0.5rem 1rem; background-color: #28a745; color: white; 
    border: none; border-radius: 4px; cursor: pointer; font-weight: 500;
  }
  .add-btn:disabled { background-color: #a8d5b5; cursor: not-allowed; }

  /* Category Filter Section */
  .category-filter-section {
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
  }
  .category-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 0.4rem;
  }
  .category-title {
    font-size: 0.85rem; font-weight: 600; color: #495057;
  }
  .clear-categories-btn {
    background: none; border: none; font-size: 0.8rem;
    color: #dc3545; cursor: pointer; padding: 0 0.2rem; font-weight: 500;
  }
  .clear-categories-btn:hover { text-decoration: underline; }
  .category-chips-container {
    display: flex; flex-wrap: wrap; gap: 0.35rem;
    max-height: 95px; overflow-y: auto; padding: 2px;
  }
  .cat-chip {
    padding: 3px 10px; border-radius: 14px; font-size: 0.8rem;
    border: 1px solid #ced4da; background: white; color: #495057;
    cursor: pointer; transition: all 0.15s ease-in-out;
  }
  .cat-chip:hover {
    border-color: #007bff; color: #007bff; background: #f0f7ff;
  }
  .cat-chip.active {
    background-color: #007bff; border-color: #007bff; color: white;
    font-weight: 600;
  }
  
  /* Selected Chips */
  .selections { margin-bottom: 0.75rem; font-size: 0.85rem; }
  .chips-container { 
    display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.4rem; 
    max-height: 80px; overflow-y: auto; 
  }
  .chip { 
    background: #e9ecef; padding: 3px 8px; border-radius: 12px; 
    font-size: 0.85rem; display: flex; align-items: center; gap: 5px; 
  }
  .remove { 
    background: none; border: none; font-weight: bold; cursor: pointer; 
    color: #dc3545; padding: 0; font-size: 1.1rem; line-height: 1; 
  }

  /* Store List */
  .store-list { 
    flex-grow: 1; overflow-y: auto; border: 1px solid #e9ecef; 
    padding: 0.6rem; border-radius: 6px; min-height: 180px; max-height: 380px;
    display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 0.5rem; align-content: start;
  }
  .store-item { 
    padding: 0.5rem 0.6rem; border: 1px solid #f1f3f5; 
    border-radius: 6px; transition: background 0.15s, border-color 0.15s;
    background: #fff;
  }
  .store-item:hover { background-color: #f8f9fa; border-color: #dee2e6; }
  .store-item.is-selected { background-color: #eef6ff; border-color: #b8daff; }
  .store-item label { 
    display: flex; align-items: flex-start; gap: 0.6rem; cursor: pointer; width: 100%; 
  }
  .store-item input[type="checkbox"] { 
    width: 1.15rem; height: 1.15rem; margin-top: 2px; flex-shrink: 0; cursor: pointer; 
  }
  .store-details {
    display: flex; flex-direction: column; gap: 0.2rem; flex-grow: 1; min-width: 0;
  }
  .store-name { font-size: 0.95rem; font-weight: 500; color: #212529; word-break: break-word; }
  .store-badges {
    display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 2px;
  }
  .badge {
    font-size: 0.72rem; padding: 1px 6px; border-radius: 10px;
    background: #e9ecef; color: #6c757d; font-weight: 500;
  }
  .badge.highlighted {
    background: #cce5ff; color: #004085; font-weight: 600;
  }

  .status-msg { text-align: center; color: #6c757d; padding: 1.5rem; margin: 0; grid-column: 1 / -1; }
  .status-msg.error { color: #dc3545; }
  .no-results { font-size: 0.9rem; color: #6c757d; font-style: italic; padding: 1rem; text-align: center; grid-column: 1 / -1; }

  .modal-actions { 
    display: flex; justify-content: flex-end; gap: 0.75rem; 
    margin-top: 1rem; border-top: 1px solid #eee; padding-top: 0.75rem; 
  }
  .primary { 
    background-color: #007bff; color: white; padding: 0.55rem 1.2rem; 
    border-radius: 4px; border: none; cursor: pointer; font-weight: 500; font-size: 0.95rem;
  }
  .primary:hover { background-color: #0069d9; }
  .secondary { 
    background-color: #6c757d; color: white; padding: 0.55rem 1.2rem; 
    border-radius: 4px; border: none; cursor: pointer; font-weight: 500; font-size: 0.95rem;
  }
  .secondary:hover { background-color: #5a6268; }
</style>